import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Snackbar
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import RefreshIcon from '@mui/icons-material/Refresh'
import { useAuth } from '../../App'
import { useTranslation } from 'react-i18next'

const API_BASE = window.__ENV__?.API_BASE || 'http://localhost:3000'

interface UserOption {
  id: number
  email: string
  label: string
}

interface EnginePricing {
  engineName: string
  engineId: number
  price: number
  isCustomPrice: boolean
  defaultPrice: number
}

const PricingTab: React.FC = () => {
  const { t } = useTranslation()
  const auth = useAuth()
  const [users, setUsers] = useState<UserOption[]>([])
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null)
  const [pricing, setPricing] = useState<EnginePricing[]>([])
  const [loading, setLoading] = useState(false)
  const [fetchingUsers, setFetchingUsers] = useState(true)
  const [fetchingPricing, setFetchingPricing] = useState(false)
  const [savingEngine, setSavingEngine] = useState<string | null>(null)
  const [editedPrices, setEditedPrices] = useState<Record<string, number>>({})
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const fetchUsers = async () => {
    setFetchingUsers(true)
    try {
      const token = await auth.getToken()
      const response = await fetch(`${API_BASE}/admin/billing/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      if (!response.ok) {
        throw new Error('Failed to fetch users')
      }
      const data = await response.json()
      setUsers(data.map((u: { id: number; email: string }) => ({
        id: u.id,
        email: u.email,
        label: `${u.email} (ID: ${u.id})`
      })))
    } catch (err) {
      setError('Failed to load users')
      console.error('Fetch users error:', err)
    } finally {
      setFetchingUsers(false)
    }
  }

  const fetchUserPricing = async (userId: number) => {
    setFetchingPricing(true)
    setError(null)
    try {
      const token = await auth.getToken()
      const response = await fetch(`${API_BASE}/admin/billing/users/${userId}/pricing`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      if (!response.ok) {
        throw new Error('Failed to fetch pricing')
      }
      const data = await response.json()
      setPricing(data)
      // Initialize edited prices with current prices
      const prices: Record<string, number> = {}
      data.forEach((p: EnginePricing) => {
        prices[p.engineName] = p.price
      })
      setEditedPrices(prices)
    } catch (err) {
      setError('Failed to load user pricing')
      console.error('Fetch pricing error:', err)
    } finally {
      setFetchingPricing(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  useEffect(() => {
    if (selectedUser) {
      fetchUserPricing(selectedUser.id)
    } else {
      setPricing([])
      setEditedPrices({})
    }
  }, [selectedUser])

  const handlePriceChange = (engineName: string, newPrice: number) => {
    setEditedPrices(prev => ({
      ...prev,
      [engineName]: newPrice
    }))
  }

  const handleSavePrice = async (engine: EnginePricing) => {
    if (!selectedUser) return

    const newPrice = editedPrices[engine.engineName]
    if (newPrice === engine.price) {
      // No change
      return
    }

    setSavingEngine(engine.engineName)
    setError(null)

    try {
      const token = await auth.getToken()
      const response = await fetch(`${API_BASE}/admin/billing/users/${selectedUser.id}/pricing`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          engineName: engine.engineName,
          price: newPrice
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to update price')
      }

      setSuccess(`Price updated for ${engine.engineName}`)

      // Refresh pricing
      await fetchUserPricing(selectedUser.id)
    } catch (err: any) {
      setError(err.message || 'Failed to update price')
      console.error('Update price error:', err)
    } finally {
      setSavingEngine(null)
    }
  }

  const formatMicroeuros = (microeuros: number) => {
    return (microeuros / 1_000_000).toFixed(6)
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('customUserPricing')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('pricingDescription')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Autocomplete
                options={users}
                value={selectedUser}
                onChange={(_, newValue) => setSelectedUser(newValue)}
                loading={fetchingUsers}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('selectUser')}
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {fetchingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              {selectedUser && (
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => fetchUserPricing(selectedUser.id)}
                  disabled={fetchingPricing}
                >
                  {t('refresh')}
                </Button>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {fetchingPricing ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : selectedUser && pricing.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('engine')}</TableCell>
                <TableCell align="center">{t('defaultPrice')}</TableCell>
                <TableCell align="center">{t('currentPrice')}</TableCell>
                <TableCell align="center">{t('newPrice')}</TableCell>
                <TableCell align="center">{t('status')}</TableCell>
                <TableCell align="center">{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pricing.map((engine) => {
                const hasChanged = editedPrices[engine.engineName] !== engine.price
                return (
                  <TableRow key={engine.engineName}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {engine.engineName}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="text.secondary">
                        {formatMicroeuros(engine.defaultPrice)} EUR/s
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {formatMicroeuros(engine.price)} EUR/s
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="number"
                        size="small"
                        value={editedPrices[engine.engineName] || 0}
                        onChange={(e) => handlePriceChange(engine.engineName, parseInt(e.target.value) || 0)}
                        inputProps={{ min: 0, style: { width: '100px', textAlign: 'right' } }}
                        disabled={savingEngine === engine.engineName}
                        helperText="microeuros/s"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {engine.isCustomPrice ? (
                        <Chip label={t('custom')} color="primary" size="small" />
                      ) : (
                        <Chip label={t('default')} variant="outlined" size="small" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={hasChanged ? t('save') : t('noChanges')}>
                        <span>
                          <IconButton
                            color="primary"
                            onClick={() => handleSavePrice(engine)}
                            disabled={!hasChanged || savingEngine === engine.engineName}
                          >
                            {savingEngine === engine.engineName ? (
                              <CircularProgress size={20} />
                            ) : (
                              <SaveIcon />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : selectedUser ? (
        <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
          {t('noEnginesAvailable')}
        </Typography>
      ) : (
        <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
          {t('selectUserToViewPricing')}
        </Typography>
      )}

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default PricingTab
